// src/components/manuscriptTheme/ThemePreview.jsx
//
// The middle of the Theme Editor: the author's own manuscript, laid out in the
// theme being edited, as two-page spreads (or every page at once), with a strip
// of thumbnails underneath. The prototype's stage.
//
// **One layout, not a sixth.** The pages come from `reflowPages`
// (src/utils/paginate.js) — the editor's own layout — run over the manuscript in
// a measuring container styled by the theme. A page is still a `.rv-page` sheet
// (src/utils/manuscriptPages.js). What the preview adds is only what a printed
// book has and an editor page does not: chapters opening on a new page (on a
// right-hand one if the theme says so), running heads and page numbers.
//
// The measuring container must be rendered to be measured, so it sits off
// screen rather than hidden. The pages are laid out at their true size and the
// stage shows them scaled, so line breaks are the same at every zoom.
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, ButtonBase, IconButton, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { tokens } from '../../theme';
import { pageSizePx, bookSize } from '../../config/bookSizes';
import { fontStackFor } from '../../config/fonts';
import { reflowPages } from '../../utils/paginate';
import { wrapAsSinglePage } from '../../utils/manuscriptPages';
import {
  BLEED_IN, CHAPTER_COUNTER, artOn, chapterLabel, completeTheme, themeCss, themeValues,
} from '../../utils/manuscriptTheme';
import { FOCUS_SELECTOR } from './themeSections';

const C = tokens.color;
const px = (points) => (points * 4) / 3;

const isChapterOpening = (block) =>
  block.getAttribute?.('data-heading-level') === '1' && !!block.textContent?.trim();

/** The theme's typefaces, loaded before anything is measured in them. */
async function ensureFonts(t) {
  if (!document.fonts?.load) return;
  const families = [...new Set([t.body.font, t.ch.lFont, t.ch.tFont, t.h.font])];
  const loads = families.flatMap((f) => ['400', 'italic 400', '600', '700']
    .map((w) => document.fonts.load(`${w} 16px "${f}"`).catch(() => {})));
  await Promise.race([Promise.all(loads), new Promise((r) => { setTimeout(r, 2500); })]);
}

// What a page is besides its text block, for both the measuring container and
// the stage: a fixed box, and none of the editor's header and footer bands.
function pageCss(scope, W, H) {
  return [
    `${scope} .rv-page{width:${W}px;height:${H}px;position:relative;display:flex;flex-direction:column;overflow:hidden;box-sizing:border-box;margin:0;flex-shrink:0}`,
    `${scope} .rv-page + .rv-page{margin-top:0}`,
    `${scope} .rv-page-header,${scope} .rv-page-footer,${scope} .rv-end-spacer{display:none !important}`,
    `${scope} .rv-page-body{flex:1 1 auto;min-height:0;overflow:hidden;box-sizing:border-box;position:relative;outline:none}`,
  ].join('\n');
}

const caseCss = (c) => (c === 'upper' ? { textTransform: 'uppercase' }
  : c === 'smallcaps' ? { textTransform: 'uppercase', fontSize: '0.8em' } : {});

const folioText = (n, style) => (style === 'dash' ? `— ${n} —` : style === 'dot' ? `· ${n} ·` : String(n));

/** Running head and page number for one page — the prototype's `pageHTML`. */
function PageChrome({ t, page, index, W, H, book }) {
  if (page.blank) return null;
  const recto = index % 2 === 0;
  const m = t.page;
  const left = (recto ? m.inside : m.outside) * 96;
  const width = W - (m.inside + m.outside) * 96;
  const topY = m.top * 96 * 0.45;
  const botY = H - m.bottom * 96 * 0.5;
  const font = fontStackFor({ label: t.ch.lFont, heading: t.h.font }[t.run.role] || t.body.font);
  const lineSx = {
    position: 'absolute', left, width, transform: 'translateY(-50%)', whiteSpace: 'nowrap', lineHeight: 1,
    fontFamily: font, fontSize: px(t.run.size), letterSpacing: `${t.run.track}em`, color: t.col.text, opacity: 0.85,
  };
  const number = index + 1;
  const fn = folioText(number, t.folio.style);
  const { mode } = t.run;
  const { pos } = t.folio;

  if (mode === 'manuscript') {
    return (
      <div className="rv-rh" style={{ ...lineSx, top: topY, textAlign: 'right' }}>
        {`${book.surname} / ${book.title.toUpperCase()} / ${number}`}
      </div>
    );
  }

  const parts = [];
  if (!page.opener && (mode !== 'none' || pos === 'top-outside')) {
    const text = mode === 'none' ? '' : mode === 'book'
      ? (recto ? book.title : book.author)
      : (recto ? page.chapterTitle : book.title);
    parts.push(
      <div key="rh" className="rv-rh" style={{ ...lineSx, top: topY, textAlign: 'center' }}>
        {pos === 'top-outside' && (
          <span className="rv-fo" style={{ position: 'absolute', top: 0, [recto ? 'right' : 'left']: 0, letterSpacing: '.04em' }}>{fn}</span>
        )}
        <span style={caseCss(t.run.case)}>{text}</span>
      </div>,
    );
  }
  const bottom = pos.startsWith('bottom') || (pos === 'top-outside' && page.opener);
  if (bottom && (!page.opener || t.folio.opener)) {
    const align = pos === 'bottom-outside' ? (recto ? 'right' : 'left') : 'center';
    parts.push(
      <div key="fo" className="rv-fo" style={{ ...lineSx, top: botY, textAlign: align, letterSpacing: '.04em' }}>{fn}</div>,
    );
  }
  return parts;
}

// The prototype's bleed guides: the trim, the bleed past it (outside edge, top
// and bottom — never the spine), and the safe area art should keep inside.
function Guides({ recto, paper }) {
  const b = BLEED_IN * 96;
  const line = (color, style = 'dashed') => `1px ${style} ${color}`;
  const bleedSides = recto ? { left: 0, right: -b } : { left: -b, right: 0 };
  return (
    <>
      {/* The sheet before trimming: paper runs into the bleed too, so art set
          to full bleed shows there as it will print, not over the stage. */}
      <div style={{ position: 'absolute', top: -b, bottom: -b, ...bleedSides, background: paper, zIndex: -1, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', inset: 0, border: line(C.info), pointerEvents: 'none', zIndex: 6 }} />
      <div
        style={{
          position: 'absolute', top: -b, bottom: -b, pointerEvents: 'none', zIndex: 6,
          border: line(tokens.chart.rose), ...(recto ? { left: 0, right: -b, borderLeft: 0 } : { left: -b, right: 0, borderRight: 0 }),
        }}
      />
      <div style={{ position: 'absolute', inset: b * 2, border: line(C.success, 'dotted'), pointerEvents: 'none', zIndex: 6 }} />
    </>
  );
}

const Page = React.memo(function Page({ page, index, t, W, H, book, guides }) {
  const recto = index % 2 === 0;
  return (
    <section
      // Which art a page carries follows these: a chapter opener, a blank page.
      className={`rv-page ${recto ? 'rv-recto' : 'rv-verso'}${page.opener ? ' rv-opener' : ''}${page.blank ? ' rv-blank' : ''}`}
      // Chapter labels count across the book, and a spread shows two pages of
      // it: each page starts the count where the book has got to.
      style={{ counterReset: `${CHAPTER_COUNTER} ${page.chaptersBefore}` }}
    >
      <div className="rv-page-body" dangerouslySetInnerHTML={{ __html: page.body }} />
      <PageChrome t={t} page={page} index={index} W={W} H={H} book={book} />
      {guides && <Guides recto={recto} paper={t.col.paper} />}
    </section>
  );
});

function Ghost({ W, H }) {
  return <div style={{ width: W, height: H, flexShrink: 0 }} />;
}

function Spread({ pair, pages, z, t, W, H, book, shadow, guides = false }) {
  // With the guides on, room round the spread for the bleed to show.
  const pad = guides ? BLEED_IN * 96 * z : 0;
  return (
    <Box sx={{ width: W * 2 * z + pad * 2, height: H * z + pad * 2, flexShrink: 0, position: 'relative' }}>
      <Box
        sx={{
          display: 'flex', width: W * 2, transformOrigin: '0 0', transform: `translate(${pad}px, ${pad}px) scale(${z})`,
          '& .rv-page': { boxShadow: shadow },
        }}
      >
        {pair.map((i, k) => (i == null
          ? <Ghost key={`g${k}`} W={W} H={H} />
          : <Page key={i} page={pages[i]} index={i} t={t} W={W} H={H} book={book} guides={guides} />))}
      </Box>
    </Box>
  );
}

const pageLabel = (i) => String(i + 1);

export default function ThemePreview({
  html, theme, trimSize, book, focus, onLayout,
}) {
  const t = useMemo(() => completeTheme(theme), [theme]);
  const themeKey = JSON.stringify(themeValues(t));
  const { width: W, height: H } = useMemo(() => pageSizePx(trimSize), [trimSize]);
  const trimHeightIn = bookSize(trimSize).heightIn;

  const measureRef = useRef(null);
  const viewRef = useRef(null);
  const [pages, setPages] = useState(null);
  const [firstWith, setFirstWith] = useState({});
  const [view, setView] = useState('spread');
  const [sp, setSp] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [stage, setStage] = useState({ w: 800, h: 600 });
  const [fontsTick, setFontsTick] = useState(0);
  const [guides, setGuides] = useState(false);
  const pendingJump = useRef(null);

  const source = useMemo(() => {
    if (!html) return wrapAsSinglePage('<p><br></p>');
    return html.includes('rv-page') ? html : wrapAsSinglePage(html);
  }, [html]);

  const measureCss = useMemo(() => `${themeCss(t, { scope: '.rv-theme-measure', trimHeightIn })}\n${pageCss('.rv-theme-measure', W, H)}`, [themeKey, trimHeightIn, W, H]); // eslint-disable-line react-hooks/exhaustive-deps
  const stageCss = useMemo(() => [
    themeCss(t, { scope: '.rv-theme-stage', trimHeightIn, sides: 'class' }),
    pageCss('.rv-theme-stage', W, H),
    // The fold: a little shade on each page's spine side.
    '.rv-theme-stage .rv-page.rv-verso::after,.rv-theme-stage .rv-page.rv-recto::after{content:"";position:absolute;top:0;bottom:0;width:36px;pointer-events:none}',
    '.rv-theme-stage .rv-page.rv-verso::after{right:0;background:linear-gradient(to left,rgba(0,0,0,.10),rgba(0,0,0,0))}',
    '.rv-theme-stage .rv-page.rv-recto::after{left:0;background:linear-gradient(to right,rgba(0,0,0,.12),rgba(0,0,0,0))}',
    // What the open section of the left panel is about, outlined.
    `.rv-theme-stage.focus-page .rv-page-body{outline:1px dashed ${C.purpleSoft};outline-offset:-1px}`,
    `.rv-theme-stage.focus-body .rv-page-body > p{background:rgba(103,95,164,.08)}`,
    ...['chapter', 'headings', 'breaks', 'quotes'].map((f) => FOCUS_SELECTOR[f].split(',').map((s) => `.rv-theme-stage.focus-${f} ${s.trim()}`).join(',')
      + `{outline:1.5px dashed ${C.purpleMid};outline-offset:5px;border-radius:2px}`),
    `.rv-theme-stage.focus-running .rv-rh,.rv-theme-stage.focus-running .rv-fo{outline:1.5px dashed ${C.purpleMid};outline-offset:5px}`,
    `.rv-theme-stage.focus-art .rv-page::before{outline:2px dashed ${C.purpleMid};outline-offset:-2px}`,
    // Bleed guides: the art may run past the page, but never past the spine.
    '.rv-theme-stage.rv-bleedview .rv-page{overflow:visible}',
    `.rv-theme-stage.rv-bleedview .rv-page.rv-verso::before{clip-path:inset(0 ${t.art.mirror ? `0 0 ${BLEED_IN * 96}px` : `${BLEED_IN * 96}px 0 0`})}`,
    `.rv-theme-stage.rv-bleedview .rv-page.rv-recto::before{clip-path:inset(0 0 0 ${BLEED_IN * 96}px)}`,
  ].join('\n'), [themeKey, trimHeightIn, W, H]); // eslint-disable-line react-hooks/exhaustive-deps

  // A font arriving late changes every measurement: lay out again.
  useEffect(() => {
    if (!document.fonts?.addEventListener) return undefined;
    const bump = () => setFontsTick((n) => n + 1);
    document.fonts.addEventListener('loadingdone', bump);
    return () => document.fonts.removeEventListener('loadingdone', bump);
  }, []);

  // ---- layout ---------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      await ensureFonts(t);
      const root = measureRef.current;
      if (cancelled || !root) return;
      root.innerHTML = source;
      reflowPages(root, {
        breakBefore: isChapterOpening,
        onRecto: t.ch.recto ? isChapterOpening : null,
      });

      const out = [];
      const first = {};
      let chapters = 0;
      let chapterTitle = '';
      root.querySelectorAll('.rv-page').forEach((pageEl, i) => {
        const body = pageEl.querySelector('.rv-page-body');
        if (!body) return;
        body.querySelectorAll('.rv-end-spacer').forEach((el) => el.remove());
        const lead = body.firstElementChild;
        const opener = Boolean(lead && isChapterOpening(lead));
        const headings = Array.from(body.children).filter(isChapterOpening);
        if (headings.length) chapterTitle = headings[0].textContent.trim();
        const blank = !body.textContent.trim() && !body.querySelector('img, .rv-scene-break, .rv-table-wrap');
        Object.entries(FOCUS_SELECTOR).forEach(([key, sel]) => {
          if (first[key] !== undefined || blank) return;
          if (!sel ? !opener : (key === 'page' || key === 'body' ? !opener : body.querySelector(sel))) first[key] = i;
        });
        out.push({
          body: body.innerHTML,
          opener,
          blank,
          chaptersBefore: chapters,
          chapterTitle,
        });
        chapters += headings.length;
      });
      if (cancelled) return;
      setPages(out);
      setFirstWith(first);
      onLayout?.({ pages: out.length, chapters });
    }, 150);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [source, themeKey, W, H, fontsTick]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- stage size, spreads, focus ------------------------------------------
  useLayoutEffect(() => {
    const el = viewRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => {
      setStage({ w: entry.contentRect.width, h: entry.contentRect.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const spreads = useMemo(() => {
    if (!pages?.length) return [];
    const list = [[null, 0]];
    for (let i = 1; i < pages.length; i += 2) list.push([i, i + 1 < pages.length ? i + 1 : null]);
    return list;
  }, [pages]);
  const spreadOf = (i) => (i <= 0 ? 0 : Math.floor((i + 1) / 2));

  useEffect(() => { pendingJump.current = focus; }, [focus]);
  useEffect(() => {
    const want = pendingJump.current;
    if (!want || !pages) return;
    if (want === 'art') {
      // The first page the art is on: an opener, a body page or a blank.
      const a = t.art;
      const i = artOn(t) ? pages.findIndex((p) => (p.opener ? a.pages.opener : p.blank ? a.pages.blank : a.pages.body)) : -1;
      if (i >= 0) { setSp(spreadOf(i)); setView('spread'); }
    } else if (firstWith[want] !== undefined) {
      setSp(spreadOf(firstWith[want]));
      setView('spread');
    }
    pendingJump.current = null;
  }, [focus, firstWith, pages, t]);

  const current = Math.max(0, Math.min(sp, spreads.length - 1));

  // Keep the spread on show in view in the strip.
  const thumbsRef = useRef(null);
  useEffect(() => {
    thumbsRef.current?.querySelector('[data-current="true"]')
      ?.scrollIntoView?.({ inline: 'center', block: 'nearest' });
  }, [current, spreads.length]);
  const step = (d) => { setView('spread'); setSp(Math.max(0, Math.min(spreads.length - 1, current + d))); };

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.closest?.('input, textarea, select, [contenteditable="true"], [role="slider"]')) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  const fit = Math.min((stage.h - 40) / H, (stage.w - 60) / (W * 2));
  const z = Math.max(0.15, fit * zoom);
  const pair = spreads[current] || [];
  const shown = pair.filter((i) => i != null);
  const chapterOf = shown.map((i) => pages?.[i]).find((p) => p && p.chapterTitle);
  const chapterNo = chapterOf ? chapterOf.chaptersBefore + (chapterOf.opener ? 1 : 0) : 0;

  const darkBtn = {
    width: 32, height: 32, borderRadius: `${tokens.radius.panel}px`,
    border: `1px solid ${C.lineOnDark}`, color: C.card, '&:hover': { background: C.lineOnDark },
  };

  return (
    <Box
      sx={{
        flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
        background: `radial-gradient(ellipse at 50% 30%, ${C.purpleDeep}, ${C.plumInk})`,
      }}
    >
      <style>{stageCss}</style>
      <style>{measureCss}</style>

      {/* The stage's own controls. They wrap rather than widen the page: at a
          phone's width or beside the docked panel there is not room for one row. */}
      <Box sx={{
        minHeight: 52, flexShrink: 0, display: 'flex', alignItems: 'center', flexWrap: 'wrap',
        columnGap: 1.5, rowGap: 1, px: 2.25, py: 1,
        color: C.onDark, fontSize: 13, borderBottom: `1px solid ${C.lineOnDark}`,
      }}
      >
        <ToggleButtonGroup
          exclusive size="small" value={view} onChange={(_, v) => v && setView(v)} aria-label="Preview layout"
          sx={{
            background: C.lineOnDark, borderRadius: `${tokens.radius.panel}px`, p: '3px',
            '& .MuiToggleButton-root': { border: 0, color: C.onDark, textTransform: 'none', fontSize: 12, fontWeight: 600, px: 1.5, py: 0.5, whiteSpace: 'nowrap', borderRadius: `${tokens.radius.base + 2}px !important` },
            '& .Mui-selected, & .Mui-selected:hover': { background: `${C.card} !important`, color: `${C.plum} !important` },
          }}
        >
          <ToggleButton value="spread">Spreads</ToggleButton>
          <ToggleButton value="grid">All pages</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '1 1 220px', minWidth: 0, justifyContent: 'center' }}>
        <IconButton size="small" aria-label="Previous spread" onClick={() => step(-1)} sx={darkBtn}><ChevronLeftIcon fontSize="small" /></IconButton>
        <Box sx={{ flex: '0 1 220px', minWidth: 0, textAlign: 'center', fontWeight: 600, color: C.card, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {shown.length ? shown.map((i) => `Page ${pageLabel(i)}`).join(' · ') : '—'}
          <Typography component="small" noWrap sx={{ display: 'block', fontWeight: 500, color: C.purpleSoft, fontSize: 11.5 }}>
            {chapterOf
              ? `${chapterLabel(t.ch.label === 'none' ? 'word' : t.ch.label, Math.max(1, chapterNo))} — ${chapterOf.chapterTitle}`
              : 'Before the first chapter'}
          </Typography>
        </Box>
        <IconButton size="small" aria-label="Next spread" onClick={() => step(1)} sx={darkBtn}><ChevronRightIcon fontSize="small" /></IconButton>
        </Box>
        {/* On a phone the spread is always fitted to the screen; zoom would only
            push it off the side. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, '@media (max-width: 640px)': { display: 'none' } }}>
          <IconButton size="small" aria-label="Zoom out" onClick={() => setZoom((v) => Math.max(0.4, v / 1.15))} sx={darkBtn}><RemoveIcon fontSize="small" /></IconButton>
          <Box sx={{ minWidth: 42, textAlign: 'center' }}>{view === 'spread' ? `${Math.round(z * 100)}%` : '—'}</Box>
          <IconButton size="small" aria-label="Zoom in" onClick={() => setZoom((v) => Math.min(4, v * 1.15))} sx={darkBtn}><AddIcon fontSize="small" /></IconButton>
          <ButtonBase onClick={() => setZoom(1)} sx={{ ...darkBtn, width: 'auto', px: 1.25, fontSize: 12 }}>Fit</ButtonBase>
        </Box>
        <ButtonBase
          onClick={() => setGuides((v) => !v)}
          aria-pressed={guides}
          title="Show trim, bleed and safe-area guides"
          sx={{ ...darkBtn, width: 'auto', px: 1.25, fontSize: 12, whiteSpace: 'nowrap', ...(guides ? { background: C.card, color: C.plum, '&:hover': { background: C.card } } : {}) }}
        >
          Bleed guides
        </ButtonBase>
      </Box>

      {/* The pages */}
      <Box
        ref={viewRef}
        className={`rv-theme-stage${focus ? ` focus-${focus}` : ''}${guides ? ' rv-bleedview' : ''}`}
        sx={{
          flex: 1, minHeight: 0, overflow: 'auto', p: 3, position: 'relative',
          display: 'flex',
          alignItems: view === 'spread' ? 'center' : 'flex-start',
          justifyContent: view === 'spread' ? 'center' : 'flex-start',
        }}
      >
        {!pages && (
          <Typography sx={{ color: C.onDark, m: 'auto' }}>Laying out your manuscript…</Typography>
        )}
        {pages && view === 'spread' && pair.length > 0 && (
          <Spread pair={pair} pages={pages} z={z} t={t} W={W} H={H} book={book} shadow={tokens.shadow.lg} guides={guides} />
        )}
        {guides && view === 'spread' && (
          <Box sx={{
            position: 'absolute', left: 18, bottom: 14, display: 'flex', gap: 1.75, fontSize: 11.5, color: C.onDark,
            background: 'rgba(0, 0, 0, 0.35)', px: 1.5, py: 0.875, borderRadius: `${tokens.radius.panel}px`,
          }}
          >
            {[[C.info, 'dashed', 'Trim'], [tokens.chart.rose, 'dashed', `Bleed ${BLEED_IN}″`], [C.success, 'dotted', 'Safe area']].map(([color, style, label]) => (
              <span key={label}>
                <i style={{ display: 'inline-block', width: 16, borderTop: `2px ${style} ${color}`, verticalAlign: 'middle', marginRight: 6 }} />
                {label}
              </span>
            ))}
          </Box>
        )}
        {pages && view === 'grid' && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '34px 30px', alignContent: 'flex-start', width: '100%' }}>
            {spreads.map((s, k) => (
              <ButtonBase
                      key={k}
                onClick={() => { setSp(k); setView('spread'); }}
                sx={{ display: 'block', contentVisibility: 'auto', '&:hover .rv-page': { outline: `6px solid ${C.purpleSoft}` } }}
              >
                <Spread pair={s} pages={pages} z={Math.min(0.5, 230 / H)} t={t} W={W} H={H} book={book} shadow={tokens.shadow.md} />
                <Typography sx={{ color: C.purpleSoft, fontSize: 11.5, mt: 1, textAlign: 'center' }}>
                  {s.filter((i) => i != null).map(pageLabel).join(' · ')}
                </Typography>
              </ButtonBase>
            ))}
          </Box>
        )}
      </Box>

      {/* Thumbnails — in the stage's scope, so they are styled like the pages */}
      <Box
        ref={thumbsRef}
        className="rv-theme-stage"
        sx={{
        height: 118, flexShrink: 0, display: 'flex', gap: 1.75, overflowX: 'auto', px: 2.25, pt: 1.5, pb: 1.25,
        borderTop: `1px solid ${C.lineOnDark}`, background: 'rgba(0, 0, 0, 0.18)',
      }}
      >
        {pages && spreads.map((s, k) => (
          <ButtonBase
              key={k}
            onClick={() => { setSp(k); setView('spread'); }}
            data-current={k === current ? 'true' : undefined}
            sx={{
              display: 'block', flexShrink: 0, textAlign: 'center', contentVisibility: 'auto',
              '& .rv-page': { outline: k === current ? `24px solid ${C.purpleSoft}` : 'none' },
            }}
          >
            <Spread pair={s} pages={pages} z={78 / H} t={t} W={W} H={H} book={book} shadow={tokens.shadow.sm} />
            <Typography sx={{ fontSize: 10.5, mt: 0.5, color: k === current ? C.card : C.purpleSoft, fontWeight: k === current ? 600 : 400 }}>
              {s.filter((i) => i != null).map(pageLabel).join('–')}
            </Typography>
          </ButtonBase>
        ))}
      </Box>

      <div
        ref={measureRef}
        className="rv-theme-measure"
        aria-hidden="true"
        style={{ position: 'absolute', left: -100000, top: 0, visibility: 'hidden', pointerEvents: 'none' }}
      />
    </Box>
  );
}
